// Import model
import { Lead2 } from '../models/Lead2';

// Import base class serializer
import { EntityOwnedModelSerializer } from './EntityOwnedModelSerializer';

// Import dependency types (models and enums used in properties)
import { Customer } from '../models/Customer';
import { SfLead } from '../models/SfLead';

// Import dependency serializers (for complex types)
import { CustomerSerializer } from './CustomerSerializer';
import { SfLeadSerializer } from './SfLeadSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Lead2 - handles JSON serialization and deserialization with type validation
 */
export class Lead2Serializer {
  /**
   * Deserializes a JSON string into a Lead2 instance
   */
  static deserialize(json: string): Lead2 {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Lead2 instance
   */
  static fromObject(obj: any): Lead2 {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Lead2');
    }


    const instance: Lead2 = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      externalId: obj['externalId'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      properties: obj['properties'] ? SfLeadSerializer.fromObject(obj['properties']) : undefined as any,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      customer: obj['customer'] ? CustomerSerializer.fromObject(obj['customer']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Lead2 instances
   */
  static deserializeArray(json: string): Lead2[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Lead2 instance to a JSON string
   */
  static serialize(instance: Lead2): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Lead2 instance to a plain object
   */
  static toObject(instance: Lead2): any {
    return {
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'externalId': instance.externalId,
      'isActive': instance.isActive,
      'properties': instance.properties ? SfLeadSerializer.toObject(instance.properties) : undefined,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'customer': instance.customer ? CustomerSerializer.toObject(instance.customer) : undefined,
    };
  }

  /**
   * Serializes an array of Lead2 instances to a JSON string
   */
  static serializeArray(instances: Lead2[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
